import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { QueryKey, useSuspenseQuery } from '@tanstack/react-query'
import { arbitrum, base, optimism, unichain } from 'viem/chains'
import { Config, useConfig } from 'wagmi'
import { z } from 'zod'

import { stusdsWithdrawDataQueryFunction, stusdsWithdrawDataQueryKey } from '../stusds/stusdsWithdrawDataQuery'
import { savingsV2WithdrawDataQueryFunction, savingsV2WithdrawDataQueryKey } from '../v2/savingsV2WithdrawDataQuery'
import {
  getSavingsWithdrawDialogFormValidator,
  validateWithdrawFromSavingsWithPsm3,
  validateWithdrawFromSavingsWithSavingsV2,
  validateWithdrawFromSavingsWithStusds,
  withdrawValidationIssueToMessage,
} from './validation'
import { DynamicValidatorConfig, ensureDynamicValidatorConfigTypes } from '@/domain/common/dynamicValidator'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { psm3Balances } from '@/features/dialogs/common/logic/psm3BalancesQuery'
import { SavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { SavingsConverter } from '@/features/savings/logic/savings-converters/types'

export type WithdrawFromSavingsValidator = z.ZodSchema<{
  symbol: string
  value: string
  isMaxSelected?: boolean | undefined
}>

export interface UseWithdrawFromSavingsValidatorParams {
  chainId: number
  timestamp: number // timestamp to validate against
  tokenRepository: SavingsTokenRepository
  savingsToken: Token
  underlyingToken: Token
  savingsTokenBalance: NormalizedNumber
  savingsConverter: SavingsConverter
}

export function useWithdrawFromSavingsValidator({
  chainId,
  timestamp,
  tokenRepository,
  savingsToken,
  underlyingToken,
  savingsTokenBalance,
  savingsConverter,
}: UseWithdrawFromSavingsValidatorParams): WithdrawFromSavingsValidator {
  const wagmiConfig = useConfig()

  const { fetchParamsQueryKey, fetchParamsQueryFn, createValidator } = getValidatorConfig({
    chainId,
    timestamp,
    tokenRepository,
    wagmiConfig,
    savingsToken,
    underlyingToken,
    savingsTokenBalance,
    savingsConverter,
  })

  const { data } = useSuspenseQuery({
    queryKey: fetchParamsQueryKey,
    queryFn: fetchParamsQueryFn,
  })

  return createValidator(data)
}

export interface GetValidatorConfigParams {
  chainId: number
  timestamp: number
  tokenRepository: SavingsTokenRepository
  wagmiConfig: Config
  savingsToken: Token
  underlyingToken: Token
  savingsTokenBalance: NormalizedNumber
  savingsConverter: SavingsConverter
}
export function getValidatorConfig({
  chainId,
  timestamp,
  tokenRepository,
  wagmiConfig,
  savingsToken,
  underlyingToken,
  savingsTokenBalance,
  savingsConverter,
}: GetValidatorConfigParams): DynamicValidatorConfig {
  if (chainId === base.id || chainId === arbitrum.id || chainId === optimism.id || chainId === unichain.id) {
    const usdc = tokenRepository.findOneBySymbol(TokenSymbol('USDC'))
    const validatorQuery = psm3Balances({ tokenRepository, wagmiConfig, chainId })

    return ensureDynamicValidatorConfigTypes({
      fetchParamsQueryKey: validatorQuery.queryKey,
      fetchParamsQueryFn: validatorQuery.queryFn,
      createValidator: ({ usds: usdsBalance, usdc: usdcBalance }) =>
        getSavingsWithdrawDialogFormValidator({
          timestamp,
          chainId,
          savingsConverter,
          savingsTokenBalance,
        }).superRefine((field, ctx) => {
          const value = NormalizedNumber(field.value === '' ? '0' : field.value)
          const isUsdcWithdraw = field.symbol === usdc.symbol
          const isMaxSelected = field.isMaxSelected
          const usdBalance = savingsConverter.convertToAssets({ shares: savingsTokenBalance })

          const issue = validateWithdrawFromSavingsWithPsm3({
            value,
            isUsdcWithdraw,
            isMaxSelected,
            user: { balance: usdBalance },
            psm3: {
              usdsBalance,
              usdcBalance,
            },
          })
          if (issue) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: issue,
              path: ['value'],
            })
          }
        }),
    })
  }

  if (savingsToken.symbol === TokenSymbol('stUSDS')) {
    return ensureDynamicValidatorConfigTypes({
      fetchParamsQueryKey: stusdsWithdrawDataQueryKey({ chainId }),
      fetchParamsQueryFn: () => stusdsWithdrawDataQueryFunction({ wagmiConfig, chainId }),
      createValidator: ({ totalSupply, totalAssets, lockstakeDebt, lockstakeDebtRate }) =>
        getSavingsWithdrawDialogFormValidator({
          timestamp,
          chainId,
          savingsConverter,
          savingsTokenBalance,
        }).superRefine((field, ctx) => {
          const value = NormalizedNumber(field.value === '' ? '0' : field.value)

          const issue = validateWithdrawFromSavingsWithStusds({
            value,
            totalSupply,
            totalAssets,
            lockstakeDebt,
            lockstakeDebtRate,
            timestamp,
            savingsConverter,
          })
          if (issue) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: withdrawValidationIssueToMessage[issue],
              path: ['value'],
            })
          }
        }),
    })
  }

  if (
    savingsToken.symbol === TokenSymbol('spUSDC') ||
    savingsToken.symbol === TokenSymbol('spUSDT') ||
    savingsToken.symbol === TokenSymbol('spETH') ||
    savingsToken.symbol === TokenSymbol('spPYUSD')
  ) {
    return ensureDynamicValidatorConfigTypes({
      fetchParamsQueryKey: savingsV2WithdrawDataQueryKey({ chainId, savingsToken }),
      fetchParamsQueryFn: () =>
        savingsV2WithdrawDataQueryFunction({
          wagmiConfig,
          savingsToken,
          underlyingToken,
          chainId,
        }),
      createValidator: ({ liquidity }) =>
        getSavingsWithdrawDialogFormValidator({
          timestamp,
          chainId,
          savingsConverter,
          savingsTokenBalance,
        }).superRefine((field, ctx) => {
          const value = NormalizedNumber(field.value === '' ? '0' : field.value)

          const issue = validateWithdrawFromSavingsWithSavingsV2({
            value,
            liquidity,
          })
          if (issue) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: issue,
              path: ['value'],
            })
          }
        }),
    })
  }

  return ensureDynamicValidatorConfigTypes({
    fetchParamsQueryKey: getCreateValidatorConfigQueryKey(savingsToken, chainId),
    fetchParamsQueryFn: () => Promise.resolve({}),
    createValidator: () =>
      getSavingsWithdrawDialogFormValidator({
        timestamp,
        chainId,
        savingsConverter,
        savingsTokenBalance,
      }),
  })
}

function getCreateValidatorConfigQueryKey(savingsToken: Token, chainId: number): QueryKey {
  return [chainId, 'dynamic-validator-withdraw-from-savings', savingsToken.symbol]
}
